import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { X } from 'lucide-angular';
import { AdsButtonComponent, AdsIconDirective } from '../ads';
import { PwaAzuriranjeService } from './pwa-azuriranje.service';

/**
 * Nenametljiva obavijest na dnu zaslona kad je nova verzija aplikacije spremna.
 * Ne prekida rad: osvježava se tek na „Osvježi”, a „Zatvori” je skriva do sljedeće verzije.
 * Živa regija (`role="status"`) je u DOM-u dok service worker radi (i prije obavijesti), pa čitač
 * zaslona najavi pojavu; bez service workera (lokalno, stari preglednik) nema ni regije.
 */
@Component({
  selector: 'app-pwa-nova-verzija',
  imports: [AdsButtonComponent, AdsIconDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (azuriranje.aktivno) {
    <div class="pwa-verzija-regija" role="status" aria-live="polite">
      @if (azuriranje.obavijestVidljiva()) {
        <div class="ds-applikator pwa-verzija">
          <p class="pwa-verzija__tekst">{{ tekst() }}</p>
          <button appAdsButton="ghost" velicina="sm" type="button" (click)="azuriranje.osvjezi()">Osvježi</button>
          @if (azuriranje.stanje() === 'spremna') {
            <button class="pwa-verzija__zatvori" type="button" aria-label="Zatvori obavijest" (click)="azuriranje.odgodi()">
              <svg [appAdsIcon]="ikonaZatvori"></svg>
            </button>
          }
        </div>
      }
    </div>
    }
  `,
  styles: `
.pwa-verzija{position:fixed;z-index:60;left:var(--gutter);right:var(--gutter);bottom:calc(16px + env(safe-area-inset-bottom,0px));
  margin-inline:auto;max-width:440px;display:flex;align-items:center;gap:12px;padding:10px 10px 10px 18px;
  background:var(--card);border-radius:var(--r-lg);box-shadow:var(--shadow-card),var(--shadow-2)}
.pwa-verzija__tekst{flex:1;min-width:0;font-size:15px;line-height:1.4;color:var(--ink)}
.pwa-verzija__zatvori{display:grid;place-items:center;flex:none;width:36px;height:36px;padding:0;border:0;border-radius:var(--r-full);
  background:transparent;color:var(--meta);font-size:18px;cursor:pointer}
.pwa-verzija__zatvori:hover{background:var(--sunk);color:var(--ink)}
.pwa-verzija__zatvori:focus-visible{outline:2px solid var(--blue);outline-offset:2px}
@media (pointer:coarse){.pwa-verzija__zatvori{width:var(--touch);height:var(--touch)}}
@media (prefers-reduced-motion:no-preference){.pwa-verzija{animation:pwa-verzija-ulaz .25s cubic-bezier(.22,.61,.36,1)}}
@keyframes pwa-verzija-ulaz{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@media (forced-colors:active){.pwa-verzija{border:1px solid CanvasText}}
`,
})
export class PwaNovaVerzijaComponent {
  protected readonly azuriranje = inject(PwaAzuriranjeService);
  protected readonly ikonaZatvori = X;
  protected readonly tekst = computed(() =>
    this.azuriranje.stanje() === 'neispravna'
      ? 'Aplikaciju treba ponovno učitati.'
      : 'Dostupna je nova verzija.',
  );
}
